import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { Voyage } from '../services/types';
import {
  selectVoyage,
  selectShip
} from '../state/voyagesData/voyagesData.actions';
import { displayAlert } from '../state/uiControl/uiControl.actions';
import Selector from './Selector';
import { YearOptions } from './ui-types';

enum EXPANDED {
  SHIP,
  YEAR,
  VOYAGE,
  DATA,
}

const fleets = [
  { name: 'Select a Ship', id: 'Default' },
  { name: 'Seabourn Quest', id: 'Quest' },
  { name: 'Seabourn Odyssey', id: 'Odyssey' },
  { name: 'Seabourn Encore', id: 'Encore' },
  { name: 'Seabourn Ovation', id: 'Ovation' },
  { name: 'Seabourn Sojourn', id: 'Sojourn' },
  { name: 'Seabourn Venture', id: 'Venture' },
  { name: 'Seabourn Pursuit', id: 'Pursuit' },
];

const queryParams = new URLSearchParams(window.location.search);
let selectedFleetIndex = fleets.findIndex((fleet) => {
  let result = false;
  queryParams.forEach((value, key) => {
    if (value.toLowerCase() === fleet.id.toLowerCase()) {
      result = true;
    }
  });
  return result;
});
if (selectedFleetIndex < 0) {
  selectedFleetIndex = 0;
}

export const MobileSelectors: React.FC<SelectorsProps> = ({
  displayAlert,
  isPositionDataLoading,
  isShipDataLoading,
  selectVoyageRequest,
  selectShipRequest,
  selectedVoyageState,
  voyages,
}) => {
  const [expanded, setExpanded] = useState<EXPANDED | null>(EXPANDED.SHIP);
  const [selectedIndexShips, setIndexShips] = useState(selectedFleetIndex);
  const [selectedIndexVoyages, setIndexVoyages] = useState(0);
  const [selectedIndexYears, setIndexYears] = useState(0);

  const toggleExpanded = (key: EXPANDED) => () => setExpanded(expanded => expanded === key ? null : key);

  // if query param for a ship is available select it
  useEffect(() => {
    if (selectedFleetIndex > 0) {
      selectShipRequest(fleets[selectedFleetIndex].id);
    }
  }, [selectShipRequest]);

  useEffect(() => {
    if (voyages.length) {
      setExpanded(EXPANDED.YEAR);
    }
  }, [voyages]);

  useEffect(() => {
    // true when selectVoyage fails
    if (!selectedVoyageState && !isPositionDataLoading) {
      setIndexVoyages(0);
    }
  }, [selectedVoyageState, isPositionDataLoading]);
  
  useEffect(() => {
    // true when selectShip fails
    if (!voyages.length && !isShipDataLoading) {
      setIndexShips(0);
    }
  }, [voyages, isShipDataLoading]);

  const years: YearOptions = useMemo(() => Array.from(
    new Set(voyages.flatMap(shipVoyages => shipVoyages.map(voyage => voyage.data.dateStart.slice(0, 4))))
  )
    .sort((a, b) => b.localeCompare(a))
    .map(year => ({ name: year, id: year }))
  , [voyages]);
  
  const yearItems = [{name: 'Select a Year', id: 'Default'}, ...years];
  const voyageItems = [
    {name: 'Select a Voyage', id: 'Default'},
    ...voyages[selectedIndexYears - 1] ?? []
  ];

  // let video;
  let selectedVoyage: Voyage | undefined;
  if (voyages[0] && selectedIndexYears > 0 && selectedIndexVoyages > 0) {
    // const currentYear = parseInt(voyages[0][0].data.dateStart.slice(0, 4));
    // for (let i = currentYear; i >= 2014; i--) {
    //   years.push({ name: i.toString(), id: i.toString() });
    // }
    // years = years.slice(0, voyages.length);


    selectedVoyage = voyages[selectedIndexYears - 1][selectedIndexVoyages - 1];
    if (selectedVoyage && selectedVoyage.data && selectedVoyage.data.video) {
      // video = selectedVoyage.data.video;
    }
  }

  const expandedProps = (key: EXPANDED) => ({
    expanded: expanded === key,
    toggleExpand: toggleExpanded(key),
  });

  return (
    <div className="grid selector-container">
      <Selector
        {...expandedProps(EXPANDED.SHIP)}
        items={fleets}
        selectItemAction={(id: string) => {
          selectShipRequest(id);
          setIndexYears(0);
          setIndexVoyages(0);
        }}
        selectedIndex={selectedIndexShips}
        setIndex={setIndexShips}
        dontListFirstItem
      />
      {years[0] && (
        <Selector
          {...expandedProps(EXPANDED.YEAR)}
          items={yearItems}
          selectItemAction={(id: string) => {
            setExpanded(EXPANDED.VOYAGE);
            setIndexVoyages(0);
          }}
          selectedIndex={selectedIndexYears}
          setIndex={setIndexYears}
          dontListFirstItem
        />
      )}
      {voyages[0] && !!selectedIndexYears && (
        <Selector
          {...expandedProps(EXPANDED.VOYAGE)}
          items={voyageItems}
          isVoyageSelector
          selectItemAction={selectVoyageRequest}
          selectedIndex={selectedIndexVoyages}
          setIndex={setIndexVoyages}
          dontListFirstItem
        />
      )}
      {fleets[selectedIndexShips].id !== 'Default' && selectedVoyage && selectedVoyage.data.video &&
        <Selector
          {...expandedProps(EXPANDED.DATA)}
          items={[
            { id: "Video Journal", name: "Video Journal" },
            { id: "Watch Video", name: "Watch Video" },
            { id: "Download Video", name: "Download Video" },
            { id: "Share Video", name: "Copy Link to Share" }]}
          selectItemAction={(id) => {
            if (id === "Watch Video") {
              window.open(selectedVoyage!.data.video!.link, '_blank');
            } else if (id === "Download Video") {
              window.open(selectedVoyage!.data.video!.downloadLink, '_blank');
            } else if (id === "Share Video") {
              navigator.clipboard.writeText(selectedVoyage!.data.video!.link);
              displayAlert({message: 'Link Copied!', severity: 'info'});
            }
          }}
          selectedIndex={0}
          dontListFirstItem
        />
      }
    </div>
  );

};

const mapStateToProps = (state: RootState) => ({
  isPositionDataLoading: state.voyagesData.isPositionDataLoading,
  isShipDataLoading: state.voyagesData.isShipDataLoading,
  selectedImage: state.uiControl.selectedImage,
  selectedVoyageState: state.voyagesData.selectedVoyageData,
  voyages: state.voyagesData.voyagesForSelectedShip,
});

const dispatchProps = {
  displayAlert,
  selectVoyageRequest: selectVoyage.request,
  selectShipRequest: selectShip.request,
};

type SelectorsProps = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

export default connect(
  mapStateToProps,
  dispatchProps
)(MobileSelectors);